import { createContext, PropsWithChildren, ReactElement, useCallback, useContext } from 'react';
import { useAuthentication } from '@onemedical/auth';
import { useGetInternalUserQuery } from '../../graphql/internalUser.generated';

import allRoles from '../../data/roles';

export type Role = {
  normalizedId: string;
  name: string;
};

export type RoleName = keyof typeof allRoles;

export const rolesContext = createContext<{
  id?: string;
  displayName?: string;
  email?: string;
  roles: Role[];
  preferredRole?: Role;
  rolesLoading: boolean;
}>({
  roles: [],
  rolesLoading: false,
});

export const useProfile = () => {
  const { id, displayName, email, roles, preferredRole } = useContext(rolesContext);

  const hasRole = useCallback(
    (role: keyof typeof allRoles) =>
      roles.some((r) => r.normalizedId === allRoles[role].toString()),
    [roles],
  );

  const primaryRole =
    preferredRole?.normalizedId || (roles.length > 0 ? roles[0].normalizedId : undefined);

  return {
    id,
    displayName,
    email,
    primaryRole,
    hasRole,
  };
};

export function ProfileProvider({
  children,
  id,
}: PropsWithChildren<{ id?: string }>): ReactElement {
  const [, { authenticated }] = useAuthentication();
  const { loading, data } = useGetInternalUserQuery({
    variables: { id },
    skip: !authenticated,
  });

  return (
    <rolesContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        id: data?.internalUser.id,
        displayName: data?.internalUser.displayName,
        email: data?.internalUser.email,
        roles: data?.internalUser.roles || [],
        preferredRole: data?.internalUser.preferredRole,
        rolesLoading: loading,
      }}
    >
      {children}
    </rolesContext.Provider>
  );
}
